<template>
  <div
    class="episode-details detail-page"
    data-test="page-episodedetail"
  >
    <PremiumBanner
      v-if="episode_data && episode_data.uuid && (episode_data.season && episode_data.season.podcast && episode_data.season.podcast.is_exclusive) && showPremiumBanner && ep_data_loaded"
      :show="episode_data"
      :title="episode_data.season.podcast.title"
      class="hidden-xs-only"
    />
    <loader
      v-show="!ep_data_loaded"
      :center="true"
      label="Fetching Episode Details.."
    />
    <div
      v-if="episode_data && ep_data_loaded"
      class="episode-details__info container"
      data-test="section-episodedetail"
    >
      <div :class="`text ${(isShowPremium && isEpisodeFree) ? 'is-free' : ''}`">
        <Breadcrumb
          v-if="breadcrumbTrail.length"
          :trail="breadcrumbTrail"
        />

        <PremiumIndicator
          v-if="episode_data.is_exclusive || (episode_data && episode_data.season && episode_data.season.podcast && episode_data.season.podcast.is_exclusive)"
        />

        <FreeEpisode
          v-if="isShowPremium && isEpisodeFree"
          :absolute="false"
        />

        <h2>{{ episode_data.title | truncate(60) }}</h2>
        <div class="episode-information">
          <span>S01 E02</span>
          <span> {{ episode_data.released_at | dateFormat }} </span>
          <span> {{ episode_data.runtime | humanizedTimeFilter }} </span>
        </div>

        <div class="show-image hidden-sm-and-up">
          <picture-component
            :src="episode_data.display_image_url"
          />
        </div>

        <div class="detail-page__buttons">
          <el-button
            v-if="isShowPremium && !isEpisodeFree && !hasAccessToPremium"
            class="listen-with-premium"
            type="primary"
            data-test="btn-premiumepisode"
            @click="show_premium_modal = true"
          >
            Listen with Premium
          </el-button>

          <el-button
            v-if="!isLoading && ((isShowPremium && hasAccessToPremium) || (isShowPremium && !hasAccessToPremium && isEpisodeFree) || !isShowPremium)"
            class="m-r btn-primary-transparent play-button icon-button"
            data-test="play-episode"
            v-on="{click: isPlaying ? playEpisode : episodeListened ? replayEpisode : playEpisode}"
          >
            <img
              v-if="!isPlaying && !episodeListened"
              src="../assets/images/ic_play_blue.svg"
            >
            <img
              v-if="!isPlaying && episodeListened"
              src="../assets/images/ic_replay_blue.svg"
            >
            <img
              v-if="isPlaying"
              src="../assets/images/ic_pause_blue.svg"
            >
            <span>{{ isPlaying ? 'Pause Episode' : episodeListened ? 'Replay' : 'Play Episode' }}</span>
          </el-button>

          <el-button
            v-if="isLoading && ((isShowPremium && hasAccessToPremium) || (isShowPremium && !hasAccessToPremium && isEpisodeFree) || !isShowPremium)"
            class="m-r btn-primary-transparent play-button icon-button"
          >
            <i class="el-icon-loading" />
            <span>Play Episode</span>
          </el-button>
        </div>

        <div class="description">
          <p
            v-if="showingMoreDescription"
            data-test="show-description"
            v-html="$sanitize(episodeDescription)"
          />
          <p
            v-if="!showingMoreDescription"
            data-test="show-description"
            v-html="$sanitize($options.filters.truncate(episodeDescription, 400))"
          />
          <el-button
            v-if="episodeDescription.length > 400"
            class="read-more"
            type="text"
            @click="toggleDescription"
          >
            {{ showingMoreDescription ? 'Less' : 'More' }} Info
            <i :class="`el-icon-arrow-${showingMoreDescription ? 'up' : 'down'}`" />
          </el-button>
        </div>

        <div class="social">
          <el-button
            v-if="isLoggedIn && ((isShowPremium && hasAccessToPremium) || (isShowPremium && !hasAccessToPremium && isEpisodeFree) || !isShowPremium)"
            :loading="adding_episode"
            class="btn-primary-transparent add-to-playlist"
            type="text"
            @click="addEpisodeToLibrary(episode_data.uuid, episode_data.runtime)"
          >
            <svg v-if="!adding_episode && !inUserLibrary" svg-inline="" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 24 24"><defs><path id="ic_add_episode_svg__a" d="M600,312 C593.372583,312 588,306.627417 588,300 C588,293.372583 593.372583,288 600,288 C606.627417,288 612,293.372583 612,300 C612,306.627417 606.627417,312 600,312 Z M600,311 C606.075132,311 611,306.075132 611,300 C611,293.924868 606.075132,289 600,289 C593.924868,289 589,293.924868 589,300 C589,306.075132 593.924868,311 600,311 Z M603.187502,296.101181 C603.187502,295.696169 602.824102,295.352274 602.357771,295.352274 L597.499183,295.352274 C597.111906,295.352274 596.812498,295.635532 596.812498,295.965812 L596.812498,303.738459 L600.0003,302.026007 L603.187502,303.738459 L603.187502,296.101181 Z M595.460224,306 L595.460224,295.965812 C595.460224,294.871411 596.381318,294 597.499183,294 L602.357771,294 C603.55477,294 604.539776,294.932138 604.539776,296.101181 L604.539776,306 L600.000254,303.561069 L595.460224,306 Z"/></defs><g fill="none" fill-rule="evenodd"><g transform="translate(-588.000000, -288.000000)"><use fill="#00AECB" xlink:href="#ic_add_episode_svg__a"/></g></g></svg>
            <svg v-if="!adding_episode && inUserLibrary" svg-inline="" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 24 24"><defs><path id="ic_add_episode_is_added_svg__a" d="M12,24 C5.372583,24 0,18.627417 0,12 C0,5.372583 5.372583,0 12,0 C18.627417,0 24,5.372583 24,12 C24,18.627417 18.627417,24 12,24 Z M15.1875021,8.10118111 L15.1875021,15.7384592 L12.0003002,14.0260066 L8.81249793,15.7384592 L8.81249793,7.9658124 C8.81249793,7.63553194 9.1119059,7.35227361 9.49918302,7.35227361 L14.3577706,7.35227361 C14.8241018,7.35227361 15.1875021,7.69616922 15.1875021,8.10118111 Z M7.46022432,18 L12.0002543,15.5610686 L16.5397757,18 L16.5397757,8.10118111 C16.5397757,6.93213817 15.5547696,6 14.3577706,6 L9.49918302,6 C8.38131769,6 7.46022432,6.87141091 7.46022432,7.9658124 L7.46022432,18 Z"/></defs><g fill="none" fill-rule="evenodd"><g transform="translate(-187.000000, -340.000000)"><circle cx="199" cy="353" r="10" fill="#FFFFFF"/><g><g transform="translate(187.000000, 340.000000)"><mask id="ic_add_episode_is_added_svg__b" fill="white"><use xlink:href="#ic_add_episode_is_added_svg__a"/></mask><g fill="#00AECB" mask="url(#ic_add_episode_is_added_svg__b)"><g transform="translate(-1.090909, -1.090909)"><rect width="25.9324675" height="25.9324675"/></g></g></g></g></g></g></svg>
            <span data-test="addToPlaylist">{{ inUserLibrary && inUserLibrary.uuid ? 'Added' : 'Add to Bookmarks' }}</span>
          </el-button>

          <SocialShareDialog
            :show-data="episode_data"
            type="episode"
          />
        </div>
      </div>

      <div class="show-image hidden-xs-only">
        <picture-component
          :src="episode_data.display_image_url"
        />
      </div>
    </div>

    <PremiumBanner
      v-if="episode_data && episode_data.uuid && (episode_data.season && episode_data.season.podcast && episode_data.season.podcast.is_exclusive) && showPremiumBanner && ep_data_loaded"
      :show="episode_data"
      :title="episode_data.season.podcast.title"
      class="hidden-sm-and-up"
    />

    <div
      v-if="similar_episodes.length"
      class="more-episodes container"
    >
      <p>More Episodes</p>
    </div>

    <div
      v-show="episode_data && episode_data.uuid"
      class="episodes-list-container container"
    >
      <div
        v-if="similar_episodes && similar_episodes.length "
        class="episodes-list"
      >
        <div v-if="ep_data_loaded && similar_episodes.length">
          <card-list-wrapper
            :episodes="similar_episodes"
            :podcast="episode_data.season.podcast"
            :bucket="{
              bucket_uuid: null,
              name: 'More Episodes',
              bucket_type:'edp_episodes',
              position: 0,
              items_count: similar_episodes.length,
              media_size: 'episode_details'
            }"
          />
        </div>
      </div>
    </div>

    <div
      v-if="episode_data && similar_episodes.length"
      class="view-more"
    >
      <router-link
        v-button-event="{buttonText: 'View More Episodes', buttonLink: 'show_detail'}"
        :to="`/listen/${episode_data.season.podcast.publisher.slug}/${episode_data.season.podcast.slug}/${episode_data.season.podcast.uuid}`"
        data-test="btn-moreepisodes"
        class="el-button btn-transparent see-all-categories-button large">
        View More Episodes
      </router-link>
    </div>

    <MoreOfCategory
      v-if="episode_data && episodeCategory"
      :category="episodeCategory"
    />
    <el-alert
      v-show="ep_data_loaded && episode_data && !episode_data.uuid"
      :closable="false"
      class="container"
      title="Episode details not found."
      type="warning"
      show-icon
      center
    />
    <login-modal
      v-if="show_login"
      :show_modal="show_login"
      @modalClosed="show_login = false"
    />
    <premium-modal
      v-if="show_premium_modal"
      :show_modal="show_premium_modal"
      @modalClosed="show_premium_modal = false"
    />
  </div>
</template>

<script>
import * as log from 'loglevel';
import Loader from '../components/Loader';
import PremiumBanner from '../components/PremiumBanner';
import PremiumContentModal from '../components/PremiumContentModal';
import LoginModal from '../components/LoginModal';
import PremiumIndicator from '../components/PremiumIndicator';
import Podcast from '../services/Podcast';
import PodcastList from '../components/PodcastList';
import User from '../services/User';
import Episode from '../services/Episode';
import CardListWrapper from '../components/CardListWrapper';
import Rating from '../components/Rating';
import SocialShareDialog from '../components/SocialShare';
import imgPlaceholder from '../assets/images/placeholder.png';
import Breadcrumb from '../components/DetailPageBreadcrumb';
import FreeEpisode from '../components/FreeEpisodeIndicator';
import MoreOfCategory from '../components/MoreOfCategory';
import SegmentEvent from '../services/PlayerEvent';
import PageMixin from '../shared/pageMixin';
import sanitizeHtml from '../shared/sanitizeHtmlString';

export default {
  name: 'EpisodeDetail',
  components: {
    Loader,
    PodcastList,
    CardListWrapper,
    Rating,
    SocialShareDialog,
    PremiumBanner,
    'premium-modal': PremiumContentModal,
    PremiumIndicator,
    Breadcrumb,
    FreeEpisode,
    MoreOfCategory,
    LoginModal,
  },
  mixins: [PageMixin],
  data() {
    return {
      imgPlaceholder,
      pageName: 'Episode Detail Page',
      show_level_displayImage: null,
      ep_data_loaded: false,
      episodes_loaded: false,
      adding_episode: false,
      // s_id: this.$route.params.s_id,
      episode_service: new Episode(),
      libraryEpisodes: [],
      user_service: new User(),
      episode_data: null,
      similar_episodes: [],
      podcast_service: new Podcast(),
      total_episodes: [],
      show_login: false,
      open_share_dialog: false,
      show_premium_modal: false,
      breadcrumbTrail: [],
      episodeCategory: null,
      eventProvider: new SegmentEvent(),
      showingMoreDescription: false,
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.loggedIn;
    },
    isLoading() {
      const { status } = this.$store.getters;

      return status === 'loading' && this.currentlyPlaying && (this.currentlyPlaying.uuid === this.episode_data.uuid);
    },
    isPlaying() {
      const { play } = this.$store.getters;

      return play && this.currentlyPlaying && (this.currentlyPlaying.uuid === this.episode_data.uuid);
    },
    currentlyPlaying() {
      return this.$store.getters.episode;
    },
    showPremiumBanner() {
      const { user } = this.user_service;

      if (!user) {
        return true;
      }

      if (user && !user.is_subscribed) {
        return true;
      }

      return false;
    },
    isShowPremium() {
      return this.episode_data && this.episode_data.season && this.episode_data.season.podcast && this.episode_data.season.podcast.is_exclusive;
    },
    hasAccessToPremium() {
      const { user } = this.user_service;

      if (!user) {
        return false;
      }

      if (user && user.is_subscribed) {
        return true;
      }

      return false;
    },
    isEpisodePremium() {
      return this.episode_data.is_exclusive;
    },
    isEpisodeFree() {
      return this.episode_data && !this.episode_data.is_exclusive;
    },
    inUserLibrary() {
      if (!this.episode_data || !this.episode_data.uuid) {
        return null;
      }
      const userEpisode = this.$store.getters.searchEpisodeInLibrary(this.episode_data.uuid);
      if (userEpisode && userEpisode.uuid) {
        return userEpisode;
      }
      return null;
    },
    episodeListened() {
      if (this.episode_data) {
        const progress = this.$store.getters.episodeProgressByUuid(this.episode_data.uuid);

        if (progress) {
          return progress.progress_percent >= 95;
        }

        return false;
      }

      return false;
    },
    episodeDescription() {
      const { description } = this.episode_data;
      const descriptionHtml = this.episode_data.description_html;
      return descriptionHtml ? sanitizeHtml(descriptionHtml) : description;
    },
  },
  beforeMount() {
    this.getEpisode();
  },
  destroyed() {
    this.setMetaInformation();
  },
  methods: {
    toggleDescription() {
      this.showingMoreDescription = !this.showingMoreDescription;
      setTimeout(() => {
        this.mapTargetBlankToAnchorTags('.episode-details__info .description a');
      }, 10);
    },
    setEpisodeCategory() {
      if (this.episode_data && this.episode_data.season && this.episode_data.season.podcast && this.episode_data.season.podcast.categories && this.episode_data.season.podcast.categories.length) {
        [this.episodeCategory] = this.episode_data.season.podcast.categories;
      }
    },
    constructBreadcrumbTrail() {
      this.breadcrumbTrail = [
        {
          name: this.episodeCategory.name,
          link: `/listen/category/${this.episodeCategory.slug}/${this.episodeCategory.uuid}`,
        },
        {
          name: this.episode_data.season.podcast.title,
          link: `/listen/${this.episode_data.season.podcast.publisher.slug}/${this.episode_data.season.podcast.slug}/${this.episode_data.season.podcast.uuid}`,
        },
        {
          name: this.episode_data.title,
        },
      ];
    },
    getEpisode() {
      const episodeUUID = this.$route.params.episodeID;
      this.episode_service.getEpisodeById(episodeUUID).then((res) => {
        log.info(res);
        this.episode_data = res;
        this.ep_data_loaded = true;
        // display show level image if episode does not have image
        if (this.episode_data && this.episode_data.season
            && this.episode_data.season.podcast) {
          this.episode_data.display_image_url = this.episode_data.display_image_url || this.episode_data.image_url || this.episode_data.season.podcast.display_image_url || this.episode_data.season.podcast.image_url || imgPlaceholder;
          this.show_level_displayImage = this.episode_data.display_image_url;
          log.info(`Image Url: ${this.episode_data.display_image_url}`);
        }
        this.getEDPEpisodes();
        this.checkCurrentEpisodeIsPlaying();

        this.setEpisodeCategory();

        if (this.episodeCategory) {
          this.constructBreadcrumbTrail();
        }
        this.eventProvider.triggerEpisodesDetailsViewedEvent(this.episode_data);

        this.setMetaInformation(
          `${this.episode_data.season && this.episode_data.season.podcast ? this.episode_data.season.podcast.title : ''}: ${this.episode_data.title} | ${this.episode_data.is_exclusive ? 'Listen Only on Luminary' : 'Luminary'}`,
          this.$options.filters.truncate(this.episode_data.description, 160),
        );
      })
        .catch((error) => {
          this.ep_data_loaded = true;
          this.showErrorMsg(error.message);
        });
    },
    fetchLeftList(list, limit) {
      const arr = [];
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i] && arr.length < limit) {
          arr.push(list[i]);
        }
      }
      arr.sort((a, b) => new Date(b.released_at) - new Date(a.released_at));
      return arr;
    },
    fetchRightList(list, limit) {
      const arr = [];
      list.forEach((item) => {
        if (item && arr.length < limit) {
          arr.push(item);
        }
      });
      return arr;
    },
    formatDate(d) {
      const a = new Date(d).toDateString().split(' ');
      return `${a[1]} ${a[2]}`;
    },
    onEpisodeClick() {
      this.playPodcast(this.episode_data);
    },
    addEpisodeToLibrary(id, runtime) {
      this.show_login = !this.isLoggedIn;
      if (this.show_login) return;
      this.show_premium_modal = this.isEpisodePremium && !this.hasAccessToPremium;
      if (this.show_premium_modal) return;

      if (this.inUserLibrary && this.inUserLibrary.uuid) {
        this.deleteEpisodeFromLibrary();
      } else {
        this.adding_episode = true;
        this.episode_service.addEpisodetoLibrary(id, runtime)
          .then((res) => {
            if (res && res.success) {
              this.$store.dispatch('setUserLibraryEpisode', { uuid: res.uuid, episode_uuid: id });
              // this.$set(this.episode_data, 'record_id', res.uuid);
            }
            this.adding_episode = false;
            this.$set(this.episode_data, 'in_library', true);

            // this.showSuccessMsg('Episode added to library');
            this.eventProvider.triggerEpisodeAddEvent(this.episode_data);
          })
          .catch((error) => {
            log.warn(error.message);
            this.adding_episode = false;

            this.showErrorMsg(error.message);
          });
      }
    },
    mapEpisodesProgress() {
      if (this.episode_data && this.episode_data.season_uuid) {
        this.ep_data_loaded = false;
        this.similar_episodes = this.similar_episodes.map((_episode) => {
          const episode = _episode;
          if (episode && episode.episode_listen && episode.episode_listen.elapsed) {
            episode.progress = episode.episode_listen.elapsed;
            episode.total_runtime = episode.episode_listen.runtime;
          }
          episode.season = {
            podcast: {
              uuid: (this.episode_data && this.episode_data.season) ? this.episode_data.season.podcast_uuid : null,
              is_exclusive: (this.episode_data && this.episode_data.season && this.episode_data.season.podcast) ? this.episode_data.season.podcast.is_exclusive : false,
            },
          };
          return episode;
        });
        this.ep_data_loaded = true;
      }
    },
    playEpisode() {
      this.loadMediaInPlayer(this.episode_data.media_url, this.episode_data.is_exclusive);
      this.show_premium_modal = this.episode_data.is_exclusive && !this.$store.getters.user.is_subscribed;
      if (this.show_premium_modal) return;
      this.$store.dispatch('setPlaylistType', 'show');

      if (!this.isPlaying && this.currentlyPlaying.uuid === this.episode_data.uuid) {
        this.$store.dispatch('play');
        this.eventProvider.triggerEpisodePlayEvent(this.episode_data, 'resume_episode');
        return;
      }

      if (this.episode_data && this.isPlaying) {
        // window.eventBus.$emit('pause-podcast')
        this.$store.dispatch('pause');
        this.eventProvider.triggerEpisodePauseEvent(this.episode_data);
        return;
      }
      // this.playPodcast(this.episode_data);
      this.$store.dispatch('setAndPlayEpisode', this.episode_data);
      this.eventProvider.triggerEpisodePlayEvent(this.episode_data);
    },
    replayEpisode() {
      const { episode } = this.$store.getters;

      if (episode.uuid === this.episode_data.uuid) {
        this.$store.dispatch('setProgress', 0);
        this.$store.dispatch('play');
      } else {
        this.$store.dispatch('setAndPlayEpisode', this.episode_data);
      }
    },
    checkCurrentEpisodeIsPlaying() {
      const currentPlayingEpisode = this.$store.getters.currentPlaylingEpisodeId;
      if (currentPlayingEpisode && this.episode_data.uuid === currentPlayingEpisode) {
        // this.$set(this.episode_data, 'is_playing', true);
      }
    },
    deleteEpisodeFromLibrary() {
      this.adding_episode = true;
      this.episode_service.deleteEpisodeFromLibrary(this.inUserLibrary.uuid).then(() => {
        this.adding_episode = false;
        // this.$set(this.episode_data, 'in_library', false);
        this.$store.dispatch('removeUserLibraryEpiosde', this.episode_data.uuid);

        // this.showSuccessMsg('Episode deleted from your library.');
        this.eventProvider.triggerEpisodeRemoveEvent(this.episode_data);
      })
        .catch((error) => {
          this.adding_podcast = false;
          log.warn(error);
        });
    },
    // Event handler to sync data after add/remove from library
    updateEpisodeStatusInLibrary(res, index, context) {
      if (this.similar_episodes && this.similar_episodes.length > index) {
        this.similar_episodes[index].record_id = res.record_id;
        this.similar_episodes[index].in_library = context === 'add';
      }
    },
    openShareDialog() {
      this.open_share_dialog = true;
    },
    hideShareDialog() {
      this.open_share_dialog = false;
    },
    savePlaylist(episodes) {
      const sortedEpisodes = episodes.sort((a, b) => new Date(a.released_at) - new Date(b.released_at));
      this.$store.dispatch('setPlaylist', sortedEpisodes);
    },
    getEDPEpisodes() {
      this.episode_service.getEdpEpisodes(this.episode_data.uuid).then((res) => {
        if (res && res.length) {
          const episodes = res;
          if (episodes && episodes.length) {
            // this.savePlaylist(episodes); // Save show episodes list in store
            const sortedEpisodes = episodes;
            // this.similar_episodes = res.episodes
            sortedEpisodes.sort((a, b) => new Date(b.released_at) - new Date(a.released_at));
            // this.similar_episodes = sortedEpisodes
            const currentEpisodeIndex = sortedEpisodes.findIndex(episode => episode.uuid === this.episode_data.uuid);
            let leftArr = [];
            let rightArr = [];
            let limit = 3;
            if (currentEpisodeIndex + 1 === sortedEpisodes.length) {
              limit = 6;
            } else if (currentEpisodeIndex + 3 >= sortedEpisodes.length) {
              limit = 6 - ((currentEpisodeIndex + 3) - (sortedEpisodes.length));
            }
            leftArr = this.fetchLeftList(sortedEpisodes.slice(0, currentEpisodeIndex), limit);
            limit = 6 - leftArr.length;
            rightArr = this.fetchRightList(sortedEpisodes.slice(currentEpisodeIndex + 1), limit);
            this.similar_episodes = leftArr.concat(rightArr);
            log.info('Simialr Episodes');
            log.info(this.similar_episodes);
            this.similar_episodes = this.similar_episodes.filter(item => item.uuid !== this.episode_data.uuid);
            // Mapping progress in Episodes
            // this.mapEpisodesProgress();
            this.mapTargetBlankToAnchorTags('.episode-details__info .description a');
          }
        }
      }).catch((error) => {
        log.info(error);
        this.mapTargetBlankToAnchorTags('.episode-details__info .description a');
      });
    },
  },
};
</script>

<style lang="scss">
@import "../assets/scss/variables.scss";

.episode-details {
  &__info {
    margin-top: 40px !important;
    padding: 0 60px;

    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .premium-indicator {
      margin-top: 25px !important;
    }

    .text {
      overflow-x: hidden;
      margin-right: 50px;

      h2 {
        margin: 15px 0 25px;
        font-size: 40px;
        font-weight: 500;
        line-height: 44px;
      }

      &.is-free {
        h2 {
          margin: 0 0 25px;
        }
      }

      .episode-information {
        margin-bottom: 25px;

        span {
          font-size: 16px;
          color: $gray;

          position: relative;

          &:not(:last-child) {
            margin-right: 32px;

            &::after {
              content: '';

              position: absolute;
              top: 50%;
              left: calc(100% + 16px);

              transform: translateY(-50%);

              height: 80%;
              width: 1px;

              background-color: $gray;
            }
          }
        }
      }

      .listen-with-premium {
        background-color: $brand;
        color: $black;
        border-color: $brand;
      }

      .play-button {
        > span {
          display: flex;
          align-items: center;
        }

        img {
          height: 22px;
          margin-right: 7px;
        }

        i {
          font-size: 22px;
        }
      }

      .description {
        > p {
          color: $black;
        }

        margin-bottom: 25px;

        /* NORMALIZE DESCRIPTION ELEMENT STYLES */
        ul,
        ol {
          margin: 0 0 0 20px;
          padding: 0;
        }

        li {
          margin: 0;
          padding: 0;
        }
      }

      @media (max-width: 767px) {
        margin-right: 0;
      }

      .social {
        display: flex;
        align-items: center;
        margin-bottom: 70px;

        @media (max-width: 767px) {
          flex-direction: column;
          align-items: flex-start;

          margin-bottom: 50px;
        }

        .add-to-playlist {
          padding-left: 0;
          padding-right: 0;

          margin-right: 50px;

          display: flex;
          align-items: center;

          i {
            font-size: 24px;
          }

          > span {
            display: flex;
            align-items: center;

            svg {
              width: 24px;
              height: 24px;

              margin-right: 10px;
            }
          }

          position: relative;

          &::after {
            content: '';

            position: absolute;
            left: calc(100% + 25px);
            top: 50%;
            transform: translateY(-50%);

            width: 1px;
            height: 40%;

            background-color: $gray;

            @media (max-width: 767px) {
              display: none;
            }
          }
        }
      }

      .read-more {
        padding: 0;
      }
    }

    .free-episode {
      margin: 25px 0 25px;
    }

    .show-image {
      position: relative;
      flex-shrink: 0;

      width: 348px;
      height: 348px;

      @media (max-width: 1200px) {
        width: 287px;
        height: 287px;
      }

      @media (max-width: 1023px) {
        width: 212px;
        height: 212px;
      }

      @media (max-width: 767px) {
        width: 100%;
        height: 100%;
        max-width: 400px;
      }

      @media (max-width: 567px) {
        width: 100%;
        height: 100%;
        max-width: none;
      }
    }

    @media (max-width: 1024px) {
      padding: 0;
    }
  }

  .more-episodes {
    > p {
      padding-left: 60px;

      font-size: 24px;
      color: $black;

      margin-bottom: 35px;

      @media (max-width: 1024px) {
        padding: 0;
      }
    }
  }

  .view-more {
    display: block;
    text-align: center;
    margin: 70px auto;
  }

  .episodes-list-container {
    background-color: $xxlight-gray;

    @media (max-width: 1024px) {
      width: 100%;
    }
  }
}
</style>



// WEBPACK FOOTER //
// app/src/pages/EpisodeDetails.vue